/* eslint-disable max-lines-per-function --
   TODO: 招募前 sprint 拆組件 (TODO-frontend-eslint-ci-warnings-as-errors)。
   2026-02-06 規則加入時 87 個既有違規凍結為 baseline，新檔/新函數仍受 hook 攔截。
*/
// frontend/src/components/Search/SearchBar.jsx
// Search input component with mode tabs (stacked layout)

import { useState, useEffect } from 'react';
import {
  Paper,
  InputBase,
  IconButton,
  Button,
  Box,
  Typography,
  Chip,
  Tooltip,
} from '@mui/material';
import SearchIcon from '@mui/icons-material/Search';
import ClearIcon from '@mui/icons-material/Clear';
import SearchModeSelector from './SearchModeSelector';
import CompactNearInput from './CompactNearInput';
import AdvancedSearchBuilder from './AdvancedSearchBuilder';
import { formatHint } from '../../utils/syntaxHint';

/**
 * SearchBar - Main search input component with mode selection
 *
 * @param {Function} onSearch - Callback when search is submitted
 * @param {string} initialQuery - Initial query string (from URL)
 * @param {string} searchMode - Current search mode ('basic' | 'advanced' | 'near')
 * @param {Function} onModeChange - Callback when mode changes
 * @param {Object} nearState - NEAR search state { keyword1, keyword2, distance }
 * @param {Function} onNearStateChange - Callback when NEAR state changes
 * @param {Array} advancedConditions - Advanced search conditions array
 * @param {Function} onAdvancedConditionsChange - Callback when conditions change
 */
const SearchBar = ({
  onSearch,
  initialQuery = '',
  searchMode = 'basic',
  onModeChange,
  nearState,
  onNearStateChange,
  advancedConditions,
  onAdvancedConditionsChange,
}) => {
  const [query, setQuery] = useState(initialQuery);
  const [showHelp, setShowHelp] = useState(false);

  // Sync external initialQuery (e.g., from URL params)
  useEffect(() => {
    if (initialQuery && initialQuery !== query) {
      setQuery(initialQuery);
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [initialQuery]);

  /**
   * Handle basic search submission
   */
  const handleBasicSubmit = (e) => {
    e.preventDefault();
    if (query.trim() && onSearch) {
      onSearch(query.trim());
    }
  };

  /**
   * Handle input change
   */
  const handleChange = (e) => {
    setQuery(e.target.value);
  };

  /**
   * Handle clear input
   */
  const handleClear = () => {
    setQuery('');
  };

  /**
   * Handle mode change
   */
  const handleModeChange = (newMode) => {
    setShowHelp(false);
    if (onModeChange) {
      onModeChange(newMode);
    }
  };

  /**
   * Render search input based on mode
   */
  const renderSearchInput = () => {
    switch (searchMode) {
      case 'near':
        return (
          <CompactNearInput
            nearState={nearState}
            onNearStateChange={onNearStateChange}
            onSearch={onSearch}
          />
        );

      case 'advanced':
        return (
          <AdvancedSearchBuilder
            conditions={advancedConditions}
            onConditionsChange={onAdvancedConditionsChange}
            onSearch={onSearch}
          />
        );

      case 'basic':
      default: {
        const hint = formatHint(query);
        return (
          <Box>
            <Paper
              component="form"
              onSubmit={handleBasicSubmit}
              sx={{
                p: '4px 6px 4px 16px',
                display: 'flex',
                alignItems: 'center',
                border: '1px solid',
                borderColor: 'divider',
                borderRadius: 2,
                boxShadow: '0 2px 8px rgba(0,0,0,0.06)',
                '&:focus-within': {
                  borderColor: 'primary.main',
                  boxShadow: '0 2px 12px rgba(139, 69, 19, 0.12)',
                },
              }}
              elevation={0}
            >
              <InputBase
                sx={{ flex: 1, height: 48, fontSize: 16 }}
                placeholder='搜尋佛法文獻...（用 "..." 精確匹配）'
                value={query}
                onChange={handleChange}
                inputProps={{ 'aria-label': '搜尋佛法文獻' }}
              />

              {/* 清除按鈕 */}
              {query && (
                <IconButton
                  size="small"
                  aria-label="clear"
                  onClick={handleClear}
                  sx={{
                    width: 28,
                    height: 28,
                    backgroundColor: 'action.hover',
                    mr: 0.5,
                  }}
                >
                  <ClearIcon sx={{ fontSize: 16 }} />
                </IconButton>
              )}

              {/* 語法說明按鈕 */}
              <Tooltip title="語法說明">
                <IconButton
                  size="small"
                  aria-label="help"
                  onClick={() => setShowHelp(!showHelp)}
                  sx={{
                    width: 28,
                    height: 28,
                    backgroundColor: 'action.hover',
                    mr: 0.5,
                    fontWeight: 600,
                    fontSize: 14,
                  }}
                >
                  ?
                </IconButton>
              </Tooltip>

              {/* 搜尋按鈕 */}
              <Button
                type="submit"
                variant="contained"
                disabled={!query.trim()}
                startIcon={<SearchIcon />}
                sx={{
                  height: 40,
                  px: 3,
                  borderRadius: 1.5,
                  textTransform: 'none',
                  fontWeight: 600,
                  fontSize: 15,
                  boxShadow: 'none',
                  '&:hover': { boxShadow: 'none' },
                }}
              >
                搜尋
              </Button>
            </Paper>

            {/* Phase E: 動態 hint（phrase / strip） */}
            {hint.type !== 'none' && (
              <Typography
                variant="caption"
                sx={{ display: 'block', mt: 0.5, ml: 1.5, color: hint.color }}
              >
                {hint.text}
              </Typography>
            )}
          </Box>
        );
      }
    }
  };

  return (
    <Box>
      {/* Mode tabs (above search bar) */}
      <Box sx={{ mb: 1.5 }}>
        <SearchModeSelector mode={searchMode} onModeChange={handleModeChange} />
      </Box>

      {/* Search input area */}
      {renderSearchInput()}

      {/* Help text (collapsible) - only show in basic mode */}
      {showHelp && searchMode === 'basic' && (
        <Paper sx={{ p: 2, mt: 2 }} elevation={1}>
          <Typography variant="subtitle2" gutterBottom>
            搜尋語法說明
          </Typography>

          <Box sx={{ mt: 1 }}>
            <Box sx={{ mb: 1 }}>
              <Chip label="一般" size="small" sx={{ mr: 1 }} />
              <Typography variant="body2" component="span">
                直接輸入關鍵字，例如：<strong>佛法</strong>
              </Typography>
            </Box>

            <Box sx={{ mb: 1 }}>
              <Chip label="AND" size="small" sx={{ mr: 1 }} color="primary" />
              <Typography variant="body2" component="span">
                使用 <strong>+</strong> 表示「兩者皆須包含」，例如：
                <strong> 佛法 + 正見</strong>
              </Typography>
            </Box>

            <Box sx={{ mb: 1 }}>
              <Chip label="OR" size="small" sx={{ mr: 1 }} color="secondary" />
              <Typography variant="body2" component="span">
                使用 <strong>/</strong> 表示「任一包含」，例如：
                <strong> 佛法 / 禪修</strong>
              </Typography>
            </Box>

            <Box sx={{ mb: 1 }}>
              <Chip label="混合" size="small" sx={{ mr: 1 }} color="success" />
              <Typography variant="body2" component="span">
                組合使用運算子，例如：
                <strong> 佛法 + 正見 / 邪見</strong>
              </Typography>
            </Box>

            <Box sx={{ mb: 1 }}>
              <Chip label="NEAR" size="small" sx={{ mr: 1 }} color="warning" />
              <Typography variant="body2" component="span">
                鄰近搜尋，例如：
                <strong> 佛法 NEAR[10] 正見</strong>（10 字內）
              </Typography>
            </Box>

            <Box sx={{ mb: 1 }}>
              <Chip label="NOT" size="small" sx={{ mr: 1 }} color="error" />
              <Typography variant="body2" component="span">
                排除特定詞，例如：
                <strong> 佛法 NOT 密宗</strong>
              </Typography>
            </Box>

            <Box>
              <Chip label="跨語言" size="small" sx={{ mr: 1 }} color="info" />
              <Typography variant="body2" component="span">
                自動搜尋繁體和簡體中文內容
              </Typography>
            </Box>
          </Box>
        </Paper>
      )}
    </Box>
  );
};

export default SearchBar;
